// Read-only view of the marketing.monitorCampaigns card.
//
// composeMonitorReport sets `data.card` to:
//   contractVersion: 1
//   kind: "campaignMonitor"
//   window: { key, label, since, until, phaseId }
//   totals, comparisons
//   windows: [{ key, label, since, until, fetchedAt, source, adAccountId, totals, comparisons }]
// Each comparison is { testId, testName, ours, control }.
// Each arm is { matched, ambiguous, matchNote, spend, impressions, clicks,
// purchases, purchaseValue, roas, currency, objects }.
// The chat tool wraps that report as kind "marketingPlanMonitor".
// This module only reads those fields. It does not call Meta.

export const CAMPAIGN_MONITOR_KIND = 'campaignMonitor';
export const CAMPAIGN_MONITOR_VERSION = 1;
export const MONITOR_TOOL_KIND = 'marketingPlanMonitor';

export const METRIC_ROWS = [
  { key: 'spend', label: 'Spend' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'clicks', label: 'Clicks' },
  { key: 'purchases', label: 'Purchases' },
  { key: 'purchaseValue', label: 'Purchase value' },
  { key: 'roas', label: 'ROAS' }
] as const;

export type MetricKey = (typeof METRIC_ROWS)[number]['key'];
export type ArmMetrics = Partial<Record<MetricKey, number>>;
export type ArmMatch = 'matched' | 'unmatched' | 'ambiguous';
export type SnapshotState = 'fresh' | 'missing' | 'rate_limited' | 'credential_error' | 'error';

export type ArmView = {
  match: ArmMatch;
  detail: string | null;
  currency: string | null;
  metrics: ArmMetrics;
};

export type ComparisonView = {
  label: string;
  ours: ArmView;
  control: ArmView;
};

export type WindowView = {
  key: string | null;
  label: string;
  since: string | null;
  until: string | null;
  takenAt: string | null;
  currency: string | null;
  campaigns: number | null;
  live: number | null;
  totals: ArmMetrics;
  comparisons: ComparisonView[];
};

export type MonitorView = {
  title: string;
  version: number | null;
  recognized: boolean;
  state: SnapshotState;
  message: string | null;
  windows: WindowView[];
};

type Rec = Record<string, unknown>;

function isRec(value: unknown): value is Rec {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function str(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function records(value: unknown): Rec[] {
  if (!Array.isArray(value)) return [];
  return value.filter(isRec);
}

/** A display string that is already safe to show. Tokens and long blobs stay hidden. */
export function redactedMessage(value: unknown): string | null {
  const text = str(value);
  if (!text || text.length > 280) return null;
  if (/bearer\s+\S{8,}/i.test(text)) return null;
  if (/EAA[A-Za-z0-9]{20,}/.test(text)) return null;
  if (/access[_-]?token/i.test(text)) return null;
  if (/\b(secret|password|api[_-]?key)\b/i.test(text)) return null;
  return text;
}

function currencyCode(value: unknown): string | null {
  const text = str(value);
  if (!text || !/^[A-Za-z]{3}$/.test(text)) return null;
  return text.toUpperCase();
}

function readMetrics(source: Rec): ArmMetrics {
  const out: ArmMetrics = {};
  for (const row of METRIC_ROWS) {
    const found = num(source[row.key]);
    if (found != null) out[row.key] = found;
  }
  return out;
}

function emptyArm(): ArmView {
  return { match: 'unmatched', detail: null, currency: null, metrics: {} };
}

function readArm(value: unknown): ArmView {
  if (!isRec(value)) return emptyArm();
  const ambiguous = value.ambiguous === true;
  const matched = value.matched === true;
  const match: ArmMatch = ambiguous ? 'ambiguous' : matched ? 'matched' : 'unmatched';
  return {
    match,
    detail: redactedMessage(value.matchNote),
    currency: currencyCode(value.currency),
    metrics: readMetrics(value)
  };
}

function comparisonLabel(row: Rec): string {
  const id = str(row.testId);
  const name = str(row.testName);
  if (name && name !== id) return id ? `Test ${id} · ${name}` : name;
  if (id) return `Test ${id}`;
  return 'Test';
}

function readComparisons(value: unknown): ComparisonView[] {
  return records(value).map((row) => ({
    label: comparisonLabel(row),
    ours: readArm(row.ours),
    control: readArm(row.control)
  }));
}

function readTotals(value: unknown): { campaigns: number | null; live: number | null; metrics: ArmMetrics } {
  if (!isRec(value)) return { campaigns: null, live: null, metrics: {} };
  return {
    campaigns: num(value.campaigns),
    live: num(value.live),
    metrics: readMetrics(value)
  };
}

function windowCurrency(comparisons: ComparisonView[], totalsCurrency: string | null): string | null {
  for (const row of comparisons) {
    if (row.ours.currency) return row.ours.currency;
    if (row.control.currency) return row.control.currency;
  }
  return totalsCurrency;
}

function readWindow(row: Rec): WindowView {
  const comparisons = readComparisons(row.comparisons);
  const totals = readTotals(row.totals);
  const since = str(row.since);
  const until = str(row.until);
  const key = str(row.key);
  return {
    key,
    label: str(row.label) ?? (since && until ? `${since} – ${until}` : key) ?? 'Window',
    since,
    until,
    takenAt: str(row.fetchedAt),
    currency: windowCurrency(comparisons, currencyCode(row.currency) ?? currencyCode(isRec(row.totals) ? row.totals.currency : null)),
    campaigns: totals.campaigns,
    live: totals.live,
    totals: totals.metrics,
    comparisons
  };
}

function versionOf(card: Rec): number | null {
  const raw = card.contractVersion;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && raw.trim() && Number.isFinite(Number(raw))) return Number(raw);
  return null;
}

function problemOf(envelope: Rec): { state: SnapshotState | null; message: string | null } {
  const last = isRec(envelope.lastError) ? envelope.lastError : null;
  const note = str(envelope.note);
  const fromNote = note ? note.match(/Last read error:\s*(.+)$/i)?.[1]?.trim() ?? null : null;
  const raw = (last ? str(last.message) : null) ?? (fromNote ? fromNote : null);
  const code = last && typeof last.code === 'number' ? last.code : null;
  let state: SnapshotState | null = null;
  if (code === 17 || (raw != null && /rate limit/i.test(raw))) state = 'rate_limited';
  else if (code === 190 || (raw != null && /credential/i.test(raw))) state = 'credential_error';
  else if (raw) state = 'error';
  return { state, message: state ? redactedMessage(raw) : null };
}

function locate(payload: unknown): { card: Rec | null; envelope: Rec } | null {
  const root = isRec(payload) ? payload : null;
  if (!root) return null;
  const data = isRec(root.data) ? root.data : null;
  const nested = data && isRec(data.card) ? data.card : null;
  const own = isRec(root.card) ? root.card : null;
  const nestedCard = [nested, own].find((candidate) => candidate?.kind === CAMPAIGN_MONITOR_KIND) ?? null;
  const card = nestedCard
    ?? (data?.kind === CAMPAIGN_MONITOR_KIND ? data : null)
    ?? (root.kind === CAMPAIGN_MONITOR_KIND ? root : null);
  if (card) return { card, envelope: data ?? root };
  if (root.kind === MONITOR_TOOL_KIND || data?.kind === MONITOR_TOOL_KIND) {
    return { card: null, envelope: data ?? root };
  }
  return null;
}

export function isCampaignMonitor(payload: unknown): boolean {
  return locate(payload) != null;
}

function windowsOf(card: Rec): WindowView[] {
  const listed = records(card.windows);
  if (listed.length) return listed.map(readWindow);
  const header = isRec(card.window) ? card.window : null;
  const comparisons = records(card.comparisons);
  if (!header && comparisons.length === 0) return [];
  return [readWindow({ ...(header ?? {}), comparisons: card.comparisons })];
}

export function readCampaignMonitor(payload: unknown): MonitorView {
  const found = locate(payload);
  const empty: MonitorView = {
    title: 'Campaign monitor',
    version: null,
    recognized: false,
    state: 'missing',
    message: null,
    windows: []
  };
  if (!found) return empty;
  const card = found.card;
  const version = card ? versionOf(card) : null;
  const header = card && isRec(card.window) ? card.window : null;
  const title = (card ? str(card.title) : null) ?? (header ? str(header.label) : null) ?? 'Campaign monitor';
  if (version != null && version !== CAMPAIGN_MONITOR_VERSION) {
    return { ...empty, title, version, recognized: false };
  }
  const windows = card ? windowsOf(card) : [];
  const problem = problemOf(found.envelope);
  const state = problem.state ?? (windows.length ? 'fresh' : 'missing');
  return {
    title,
    version: version ?? CAMPAIGN_MONITOR_VERSION,
    recognized: true,
    state,
    message: problem.message,
    windows
  };
}
